/**
 * Marker artwork and geometry, shared between the live map (DOM markers built
 * in MapComponent) and server-side map images (the /api/pin-marker.svg route
 * consumed by the mapshot sidecar). Pure data + string building — safe to
 * import from both the browser and the server.
 */

/** What is being reported at the pin. Absent on pins created before this existed. */
export type PinKind = 'foot' | 'car' | 'velo' | 'horse'

/** Kinds that move along a heading and so carry a direction arrow. */
const DIRECTIONAL_KINDS: PinKind[] = ['foot', 'car', 'velo', 'horse']

/**
 * Kinds whose icon itself turns to face the heading (side-view vehicles and
 * animals). The pedestrian icon is front-facing, so it keeps upright and only
 * the arrow moves.
 */
const ROTATING_KINDS: PinKind[] = ['car', 'velo', 'horse']

export function isDirectional(kind: PinKind | undefined): boolean {
  return kind !== undefined && DIRECTIONAL_KINDS.includes(kind)
}

export function rotatesBody(kind: PinKind | undefined): boolean {
  return kind !== undefined && ROTATING_KINDS.includes(kind)
}

// The mounted-rider glyph is custom (no lucide equivalent), drawn on a 256
// grid. Kept as bare paths so the marker and picker button render from one
// source. Filled, unlike the stroked vehicle icons.
export const HORSE_VIEWBOX = 256
export const HORSE_PATHS: { id: string; d: string }[] = [
  {
    id: 'rider',
    d: 'M100,38.1c9.6,0,17.4-7.8,17.4-17.4c0-9.6-7.8-17.4-17.4-17.4c-9.6,0-17.4,7.8-17.4,17.4C82.7,30.3,90.4,38.1,100,38.1z',
  },
  {
    id: 'body',
    d: 'M229.3,88.2l-18.4-3.5L200.1,106c1.7,0.4,3.5,0.6,5.4,0.6C217,106.6,226.6,98.8,229.3,88.2z M2.8,124.2c0-14.1,11.4-25.6,25.6-25.6h54V59.9c0-9.6,7.8-17.4,17.4-17.4s17.4,7.8,17.4,17.4v6l25.7,6.6c4.5,1,7.4,5.5,6.3,10c-1,4.6-5.5,7.4-10.1,6.3l-22-5.2v15H143l70.8-73.2l-2.1,19l34.9,23.2c5.7,3.3,7.8,10.7,4.5,16.5c-2.6,4.6-7.7,6.8-12.6,5.9l-2.8-0.5c-3.4,13.6-15.6,23.6-30.2,23.6c-2.9,0-5.7-0.4-8.3-1.1l-26.4,54v74c0,6.3-5.2,11.5-11.6,11.5c-6.3,0-11.5-5.2-11.5-11.5v-75.7H89.5L79.1,182l21.8,52c3.2,5.6,1.2,12.6-4.3,15.7c-5.6,3.2-12.6,1.2-15.8-4.3l-25.5-60v-19.1L44,184.2v55.7c0,6.3-5.1,11.5-11.5,11.5c-6.3,0-11.6-5.2-11.6-11.5v-64.3l9.5-14.9v-50.5L21,124.7v42H2.8V124.2z',
  },
]

// Marker glyph: scaled from the 256 grid into the shared 24x24 pin viewBox.
export const HORSE_PARTS =
  `<g transform="scale(${24 / HORSE_VIEWBOX})">` +
  HORSE_PATHS.map(({ d }) => `<path d="${d}"/>`).join('') +
  `</g>`

// Icon bodies from lucide-react (`PersonStanding`, `Car`, `Bike`) plus the
// custom horse, inlined so the markers use the same artwork as the buttons.
export const PIN_ICON_SVG: Record<PinKind, string> = {
  foot: `<circle cx="12" cy="5" r="1"/><path d="m9 20 3-6 3 6"/><path d="m6 8 6 2 6-2"/><path d="M12 10v4"/>`,
  car: `<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>`,
  velo: `<circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/>`,
  // Filled, so it opts out of the parent svg's stroke styling
  horse: `<g fill="#000000" stroke="none">${HORSE_PARTS}</g>`,
}

/** The direction arrow, identical in the orbiting drag handle and static images. */
export const GRIP_ARROW_INNER = `<path d="M12 2.5 L18.5 19 L12 15.2 L5.5 19 Z" fill="#000000" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/>`

/**
 * A standalone SVG of the marker exactly as the live map draws it at map
 * bearing 0: the 40px icon body (rotated to face `heading` for side-view
 * kinds, mirrored past due south so it never drives on its roof) and the
 * direction arrow orbiting at the same offset as the drag handle. `heading`
 * null means parked: body upright, no arrow. A translucent halo keeps the
 * thin strokes readable once flattened into a JPEG.
 *
 * Coordinate space matches the DOM marker: 88x88 with the pin position at the
 * center — composite it center-anchored.
 *
 * `timeLabel` reproduces the pin-time element: the creation clock time under
 * the marker, black with a white halo (SVG paint-order stands in for the DOM
 * text-shadow).
 */
export function pinMarkerSvg(
  kind: PinKind,
  heading: number | null | undefined,
  timeLabel = ''
): string {
  const angle =
    typeof heading === 'number' ? ((heading % 360) + 360) % 360 : null

  // Mirrors applyHeadingTransforms in MapComponent (bearing = 0).
  let bodyTransform = ''
  if (angle !== null && rotatesBody(kind)) {
    const upsideDown = angle > 180 && angle < 360
    bodyTransform = `rotate(${angle - 90})${upsideDown ? ' scale(1 -1)' : ''}`
  }

  // Arrow center sits 28px above the pin center (the drag handle's grip box),
  // swung around the pin by the heading.
  const arrow =
    angle !== null && isDirectional(kind)
      ? `<g transform="rotate(${angle})"><svg x="-11" y="-39" width="22" height="22" viewBox="0 0 24 24">${GRIP_ARROW_INNER}</svg></g>`
      : ''

  // Body bottom edge is y=20; the DOM label sits 1px below with an 11px font.
  const label = timeLabel
    ? `<text y="31" text-anchor="middle" font-family="DejaVu Sans, sans-serif" font-size="11" font-weight="600" fill="#000000" stroke="#ffffff" stroke-width="3" stroke-linejoin="round" paint-order="stroke">${timeLabel}</text>`
    : ''

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="88" height="88" viewBox="-44 -44 88 88">` +
    `<circle r="19" fill="#ffffff" opacity="0.75"/>` +
    `<g transform="${bodyTransform}">` +
    `<svg x="-20" y="-20" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${PIN_ICON_SVG[kind]}</svg>` +
    `</g>` +
    arrow +
    label +
    `</svg>`
  )
}
